import React, { PureComponent } from 'react';
import { Button, Col, Input, Row, Select } from 'antd';
import { MeasureSubType, MeasureType, ReroutingDto } from '../../../model/CORE';
import InputTime from '../../../common/input/InputTime';
import { Flex } from '../../common/Flex';
import MeasureUtils from '../../../common/util/MeasureUtils';
import TimeUtils from '../../../common/util/TimeUtils';
import ConditionalRender from '../../../common/components/ConditionalRender';
import AppContext from 'app/common/context/AppContext';

interface MiniReroutingEditorPanelProps {
    rerouting: ReroutingDto;
    onReroutingUpdated?: (rerouting: ReroutingDto) => void;
    readonly?: boolean;
}

interface MiniReroutingEditorPanelState {
    rerouting: ReroutingDto;
}

export class MiniReroutingEditorPanel extends PureComponent<
    MiniReroutingEditorPanelProps,
    MiniReroutingEditorPanelState
> {
    constructor(props) {
        super(props);
        this.state = {
            rerouting: props.rerouting,
        };
    }

    componentDidUpdate(prevProps) {
        if (this.props?.rerouting !== prevProps?.rerouting) {
            this.setState({ rerouting: this.props?.rerouting });
        }
    }

    updateRerouting = <K extends keyof MiniReroutingEditorPanelState>(
        update: Partial<ReroutingDto>,
        stateUpdate?: Partial<MiniReroutingEditorPanelState>,
    ) => {
        const updatedRerouting = Object.assign({}, this.state.rerouting, update);
        const updatedState: Pick<MiniReroutingEditorPanelState, K> = Object.assign(
            {},
            { rerouting: updatedRerouting },
            stateUpdate as Pick<MiniReroutingEditorPanelState, K>,
        );
        this.setState(updatedState, () => this.props.onReroutingUpdated?.(updatedRerouting));
    };

    updateIdentifier = (id: string) => {
        this.updateRerouting({
            measureId: {
                id: id,
                type: this.state.rerouting?.measureId?.type || MeasureType.REROUTING,
            },
        });
    };

    updateStartTime = (startTime: Date) => {
        let from = null;

        if (startTime) {
            // Start time date has to be TODAY
            from = TimeUtils.mergeDateAndTime(AppContext.getMonitoredDay(), startTime);
        }
        return this.updateRerouting({
            applicability: {
                from: from,
                to: this.state.rerouting?.applicability?.to,
            },
        });
    };

    updateEndTime = (endTime: Date) => {
        let to = null;
        if (endTime) {
            // End time date will either be the day of the start time, or the next day, or TODAY()
            let day =
                (this.state.rerouting?.applicability?.from &&
                    TimeUtils.withoutTimeOfDay(this.state.rerouting?.applicability?.from)) ||
                AppContext.getMonitoredDay();
            if (
                this.state.rerouting?.applicability?.from &&
                TimeUtils.timeSinceMidnightInMs(endTime) <=
                    TimeUtils.timeSinceMidnightInMs(this.state.rerouting?.applicability?.from)
            ) {
                day = TimeUtils.addDays(day, 1);
            }
            to = TimeUtils.addMillis(day, TimeUtils.timeSinceMidnightInMs(endTime));
        }
        return this.updateRerouting({
            applicability: {
                from: this.state.rerouting?.applicability?.from,
                to: to,
            },
        });
    };

    render() {
        const tvId = this.state.rerouting && MeasureUtils.getTvLocation(this.state.rerouting).id;
        return (
            <Flex direction="vertical" style={{ fontWeight: 'bold' }}>
                <Row gutter={[8, 8]}>
                    <Col span={3} className={`${this.props.readonly ? 'disabled' : ''}`}>
                        Identifier
                        <Input
                            disabled={true}
                            value={this.state.rerouting?.measureId?.id}
                            onChange={value => this.updateIdentifier(value.currentTarget.value)}
                        />
                    </Col>
                    <Col span={5} className={`${this.props.readonly ? 'disabled' : ''}`}>
                        Reason
                        <Select value={this.state.rerouting.subType} listHeight={320} style={{ width: '100%' }}>
                            <Select.Option value={MeasureSubType.AIRBORNE_HORIZONTAL_REROUTING}>
                                Airborne Horizontal Rerouting
                            </Select.Option>
                            <Select.Option value={MeasureSubType.AIRBORNE_LEVEL_CAP}>Airborne Level Cap</Select.Option>
                            <Select.Option value={MeasureSubType.GROUND_DELAY}>Ground Delay</Select.Option>
                            <Select.Option value={MeasureSubType.GROUND_HORIZONTAL_REROUTING}>
                                Ground Horizontal Rerouting
                            </Select.Option>
                            <Select.Option value={MeasureSubType.GROUND_LEVEL_CAP}>Ground Level Cap</Select.Option>
                            <Select.Option value={MeasureSubType.MILES_MINUTES_IN_TRAIL}>Miles In Trail</Select.Option>
                            <Select.Option value={MeasureSubType.MINIMUM_DEPARTURE_INTERVAL}>
                                Minimum Departure Interval
                            </Select.Option>
                            <Select.Option value={MeasureSubType.OTHER_KIND_OF_STAM_MEASURE}>
                                Other Kind of STAM Measure
                            </Select.Option>
                            <Select.Option value={MeasureSubType.TAKE_OFF_NOT_AFTER}>Take-off Not After</Select.Option>
                            <Select.Option value={MeasureSubType.TAKE_OFF_NOT_BEFORE}>
                                Take-off Not Before
                            </Select.Option>
                            <Select.Option value={MeasureSubType.TERMINAL_PROCEDURE_CHANGE}>
                                Terminal Procedure Change
                            </Select.Option>
                        </Select>
                    </Col>
                    <Col span={4} className={`${this.props.readonly ? 'disabled' : ''}`}>
                        TFV
                        <Input value={tvId} />
                    </Col>
                    <Col span={2} className={`${this.props.readonly ? 'disabled' : ''}`}>
                        Start time
                        <InputTime
                            value={this.state.rerouting?.applicability?.from}
                            onChange={value => this.updateStartTime(value)}
                        />
                    </Col>
                    <Col span={2} className={`${this.props.readonly ? 'disabled' : ''}`}>
                        End time
                        <InputTime
                            value={this.state.rerouting?.applicability?.to}
                            onChange={value => this.updateEndTime(value)}
                        />
                    </Col>
                    <ConditionalRender condition={!this.props.readonly}>
                        <Col span={7} style={{ alignSelf: 'center', textAlign: 'end' }}>
                            <Button>ADVANCED EDIT</Button>
                        </Col>
                    </ConditionalRender>
                </Row>
            </Flex>
        );
    }
}
